import { useState, useEffect } from "react";
import { LogOut, Save, Bell, Crown } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { PATTERN_LABELS, CURRENCIES, LANGUAGES } from "@/i18n/translations";
import { SHIFT_META } from "@/lib/shifts";
import { remindersEnabled, setRemindersEnabled, requestPermission } from "@/lib/reminders";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import TeamSection from "@/components/TeamSection";

const PATTERNS = {
  classic_rotating: ["morning", "morning", "afternoon", "afternoon", "night", "night", "off", "off"],
  mans_off: ["morning", "afternoon", "night", "off"],
  five_mornings: ["morning", "morning", "morning", "morning", "morning", "off", "off"],
  continental: ["morning", "morning", "afternoon", "afternoon", "night", "night", "off"],
  nights_block: ["night", "night", "night", "off", "off"],
};

export default function SettingsView({ settings, onSaved }) {
  const { t, lang, setLang } = useI18n();
  const { logout, user } = useAuth();
  const { isPro, sub, openPaywall, refreshSub } = useSubscription();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [subBusy, setSubBusy] = useState(false);
  const [remOn, setRemOn] = useState(remindersEnabled());

  useEffect(() => { if (settings) setForm({ ...settings }); }, [settings]);

  if (!form) return null;
  const labels = PATTERN_LABELS[lang] || PATTERN_LABELS.en;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTime = (shift, field, value) =>
    setForm((f) => ({ ...f, shift_times: { ...f.shift_times, [shift]: { ...f.shift_times[shift], [field]: value } } }));

  const toggleReminders = async (on) => {
    if (on && !isPro) { openPaywall(); return; }
    if (on) {
      const p = await requestPermission();
      if (p === "granted") { setRemindersEnabled(true); setRemOn(true); toast.success(t("remindersOn")); }
      else { setRemOn(false); toast.error(t("remindersBlocked")); }
    } else { setRemindersEnabled(false); setRemOn(false); }
  };

  const cancelSub = async () => {
    setSubBusy(true);
    try { await api.post("/subscription/cancel"); await refreshSub(); toast.success(t("saved")); }
    catch { toast.error("Error"); } finally { setSubBusy(false); }
  };
  const resumeSub = async () => {
    setSubBusy(true);
    try { await api.post("/subscription/resume"); await refreshSub(); toast.success(t("saved")); }
    catch { toast.error("Error"); } finally { setSubBusy(false); }
  };
  const periodEnd = sub?.current_period_end
    ? new Date(sub.current_period_end * 1000).toLocaleDateString(lang, { day: "numeric", month: "short", year: "numeric" })
    : null;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        currency: form.currency,
        hourly_rate: Number(form.hourly_rate),
        night_diff_pct: Number(form.night_diff_pct),
        overtime_threshold_weekly: Number(form.overtime_threshold_weekly),
        overtime_multiplier: Number(form.overtime_multiplier),
        pattern: PATTERNS[form.pattern_name],
        pattern_name: form.pattern_name,
        shift_times: form.shift_times,
        language: lang,
      });
      toast.success(t("saved"));
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <h2 className="font-display text-2xl font-bold">{t("setTitle")}</h2>

      <div className="rounded-xl border border-white/10 bg-card p-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("account")}</p>
        <p className="mt-1 font-semibold">{user?.name}</p>
        <p className="text-sm text-muted-foreground">{user?.email}</p>
      </div>

      <div className="rounded-xl border p-4" data-testid="subscription-card"
        style={{ borderColor: isPro ? "hsl(var(--primary) / 0.5)" : "rgba(255,255,255,0.1)" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Crown className={`h-5 w-5 ${isPro ? "text-primary" : "text-muted-foreground"}`} />
            <div>
              <p className="font-semibold text-sm">{t("manageSub")}</p>
              <p className="text-xs text-muted-foreground">
                {isPro ? (sub?.status === "trialing" ? t("trialing") : t("proActive")) : "Free"}
              </p>
            </div>
          </div>
          {isPro ? (
            <span className="rounded-full bg-primary px-2.5 py-1 text-[11px] font-bold text-primary-foreground">{t("pro")}</span>
          ) : (
            <Button data-testid="settings-upgrade-btn" onClick={openPaywall} className="h-9 px-4 font-semibold">
              {t("unlock")}
            </Button>
          )}
        </div>
        {isPro && periodEnd && (
          <p className="text-xs text-muted-foreground mt-3">
            {sub?.cancel_at_period_end ? t("endsOn") : t("renewsOn")} {periodEnd}
          </p>
        )}
        {isPro && (
          sub?.cancel_at_period_end ? (
            <Button variant="outline" data-testid="settings-resume-sub-btn" onClick={resumeSub} disabled={subBusy}
              className="mt-3 w-full h-10 border-white/15">{t("resumePlan")}</Button>
          ) : (
            <Button variant="outline" data-testid="settings-cancel-sub-btn" onClick={cancelSub} disabled={subBusy}
              className="mt-3 w-full h-10 border-white/15 text-destructive hover:text-destructive">{t("cancelPlan")}</Button>
          )
        )}
      </div>

      <TeamSection />

      <div className="flex items-center justify-between rounded-xl border border-white/10 bg-card p-4" data-testid="reminders-card">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center">
            <Bell className="h-4 w-4" />
          </div>
          <div>
            <p className="font-semibold text-sm">{t("reminders")}</p>
            <p className="text-xs text-muted-foreground">{t("remindersDesc")}</p>
          </div>
        </div>
        <Switch data-testid="reminders-toggle" checked={remOn} onCheckedChange={toggleReminders} />
      </div>

      <div className="space-y-2">
        <Label>{t("language")}</Label>
        <div className="grid grid-cols-5 gap-2">
          {LANGUAGES.map((l) => (
            <button key={l.code} data-testid={`settings-lang-${l.code}`} onClick={() => setLang(l.code)}
              className={`rounded-lg border py-2 text-lg transition-colors ${lang === l.code ? "border-primary bg-secondary/60" : "border-white/10"}`}>
              {l.flag}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t("rotation")}</Label>
        <div className="space-y-2">
          {Object.keys(PATTERNS).map((key) => (
            <button key={key} data-testid={`settings-pattern-${key}`} onClick={() => set("pattern_name", key)}
              className={`w-full text-left rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${form.pattern_name === key ? "border-primary bg-secondary/60" : "border-white/10"}`}>
              {labels[key]}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t("currency")}</Label>
        <div className="grid grid-cols-4 gap-2">
          {CURRENCIES.map((c) => (
            <button key={c.code} data-testid={`settings-currency-${c.code}`} onClick={() => set("currency", c.code)}
              className={`rounded-lg border py-2 text-sm font-semibold transition-colors ${form.currency === c.code ? "border-primary bg-secondary/60" : "border-white/10"}`}>
              {c.symbol}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t("shiftTimes")}</Label>
        {["morning", "afternoon", "night"].map((s) => (
          <div key={s} className="flex items-center gap-2 rounded-lg border border-white/10 p-3">
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: SHIFT_META[s].color }} />
            <span className="text-sm font-medium w-20 shrink-0">{t(s)}</span>
            <Input type="time" data-testid={`settings-start-${s}`} value={form.shift_times?.[s]?.start || ""}
              onChange={(e) => setTime(s, "start", e.target.value)} className="bg-secondary/70 border-white/10 h-10" />
            <span className="text-muted-foreground text-xs">–</span>
            <Input type="time" data-testid={`settings-end-${s}`} value={form.shift_times?.[s]?.end || ""}
              onChange={(e) => setTime(s, "end", e.target.value)} className="bg-secondary/70 border-white/10 h-10" />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>{t("hourlyRate")}</Label>
          <Input type="number" step="0.5" data-testid="settings-rate" value={form.hourly_rate}
            onChange={(e) => set("hourly_rate", e.target.value)} className="bg-secondary/70 border-white/10 h-11" />
        </div>
        <div className="space-y-1.5">
          <Label>{t("nightDiff")}</Label>
          <Input type="number" step="5" data-testid="settings-nightdiff" value={form.night_diff_pct}
            onChange={(e) => set("night_diff_pct", e.target.value)} className="bg-secondary/70 border-white/10 h-11" />
        </div>
        <div className="space-y-1.5">
          <Label>{t("overtimeThreshold")}</Label>
          <Input type="number" step="1" data-testid="settings-ot-threshold" value={form.overtime_threshold_weekly}
            onChange={(e) => set("overtime_threshold_weekly", e.target.value)} className="bg-secondary/70 border-white/10 h-11" />
        </div>
        <div className="space-y-1.5">
          <Label>{t("overtimeMultiplier")}</Label>
          <Input type="number" step="0.1" data-testid="settings-ot-mult" value={form.overtime_multiplier}
            onChange={(e) => set("overtime_multiplier", e.target.value)} className="bg-secondary/70 border-white/10 h-11" />
        </div>
      </div>

      <Button data-testid="settings-save-btn" onClick={save} disabled={saving} className="w-full h-12 font-semibold">
        <Save className="h-4 w-4 mr-1.5" /> {saving ? t("processing") : t("save")}
      </Button>

      <Button variant="outline" data-testid="settings-logout-btn" onClick={logout}
        className="w-full h-12 border-white/15 text-destructive hover:text-destructive">
        <LogOut className="h-4 w-4 mr-1.5" /> {t("logout")}
      </Button>
    </div>
  );
}
